import { useSpring, config } from '@react-spring/core';
import { animated } from '@react-spring/web';
import React, { VoidFunctionComponent, ComponentProps } from 'react';
import { useEventCallback } from '../../hooks/useEventCallback';
import { Icon, IconSize, IconType } from '../../misc/Icon/Icon';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import {
  Notification,
  NotificationIconType,
  NotificationType
} from '../classes/NotificationManager/NotificationManager';
import styles from './NotificationCard.module.css';

export type NotificationCardProps = PropsWithoutChildren<
  Omit<ComponentProps<'div'>, 'style' | 'ref'>
> & {
  notification: Notification;
  onCloseButtonClick: (notification: Notification) => void;
  onHidden: (notification: Notification) => void;
};

export const NotificationCard: VoidFunctionComponent<NotificationCardProps> =
  React.memo((props) => {
    const {
      notification,
      className,
      onCloseButtonClick,
      onHidden,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });
    const iconConfig = iconConfigByType[notification.iconType];

    const style = useSpring({
      from: { transform: 'translateX(20%)', opacity: 0 },
      to: { transform: 'translateX(0%)', opacity: 1 },
      reverse: !notification.show,
      config: { ...config.stiff, restVelocity: 0.005 },
      onRest: () => {
        if (!notification.show) {
          onHidden(notification);
        }
      }
    });

    const handleCloseButtonClick = useEventCallback(() => {
      onCloseButtonClick(notification);
    });

    return (
      <animated.div
        {...remainingProps}
        className={classes(
          className,
          styles.card,
          cardClassNameByType[notification.type]
        )}
        style={style}
      >
        <div className={styles.titleRow}>
          {iconConfig ? (
            <Icon
              {...iconConfig}
              className={styles.icon}
              data-testid="NotificationCard.Icon"
            ></Icon>
          ) : undefined}
          <div className={styles.title}>{notification.title}</div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleCloseButtonClick}
            data-testid="NotificationCard.CloseButton"
          >
            <Icon
              name="times"
              size={IconSize.Small}
            ></Icon>
          </button>
        </div>
        {notification.message ? (
          <div
            className={classes(
              styles.message,
              iconConfig ? styles.messageWithIcon : null
            )}
          >
            {notification.message}
          </div>
        ) : undefined}
      </animated.div>
    );
  });

const cardClassNameByType: Record<NotificationType, string | null> = {
  [NotificationType.Info]: styles.infoCard,
  [NotificationType.Success]: styles.successCard,
  [NotificationType.Error]: styles.errorCard
};

const iconConfigByType: Record<
  NotificationIconType,
  { name: string; type: IconType } | null
> = {
  [NotificationIconType.None]: null,
  [NotificationIconType.Success]: {
    name: 'check-circle',
    type: IconType.Regular
  },
  [NotificationIconType.Info]: {
    name: 'info-circle',
    type: IconType.Regular
  },
  [NotificationIconType.Error]: {
    name: 'times-circle',
    type: IconType.Regular
  }
};
